import { Mutation, useMutation, useMutationState } from '@tanstack/react-query';
import _ from 'lodash';
import { useContext } from 'react';
import { useIntl } from 'react-intl';

import { PACSCommunicationContext } from 'app/components/PACSCommunicationProvider/context';
import { PACSStoreError } from 'app/dicom/syncImageWithPACS';
import syncStudyFromStoreWithPACS from 'app/dicom/syncStudyFromStoreWithPACS';
import { Study } from 'app/interfaces/Study';
import DicomBuilderContext from 'app/providers/DicomBuilder/context';
import IImageProcessorContext from 'app/providers/ImageProcessorProvider/context';
import StudyStoreContext from 'app/providers/StudyStoreProvider/context';
import { isOk } from 'app/type-utils/Result';
import { LocalDicomStoreContext } from 'app/providers/LocalDicomStoreProvider';
import { useSelector } from 'react-redux';
import { selectClinicInfo } from 'app/redux/clinicInfo/reducer';

export class PACSSyncFailuresError extends Error {
  constructor(public syncFailures: Record<string, PACSStoreError>) {
    super(
      `Sync failures: ${_.map(syncFailures, (failure) => {
        if (failure instanceof Error) {
          return failure.message;
        }
        return failure.status;
      }).join(', ')}`
    );
    this.name = 'SyncFailuresError';
  }
}

type MutationFnVariables = {
  study: Study;
};

export default function useSyncStudyFromStoreWithPACSMutation() {
  const intl = useIntl();
  const localDicomStore = useContext(LocalDicomStoreContext);
  const studyStore = useContext(StudyStoreContext);
  const imageProcessor = useContext(IImageProcessorContext);
  const pacsCommunication = useContext(PACSCommunicationContext);
  const dicomBuilder = useContext(DicomBuilderContext);
  const clinicInfo = useSelector(selectClinicInfo);

  return useMutation({
    mutationKey: ['syncStudyFromStoreWithPACS'],
    /**
     * @throws {PACSSyncFailuresError}
     * This mutation will throw a PACSSyncFailuresError if any of the sync requests fail.
     */
    mutationFn: async (variables: MutationFnVariables) => {
      const { study } = variables;

      const syncRequests = await syncStudyFromStoreWithPACS(study, {
        intl,
        studyStore,
        imageProcessor,
        pacsCommunication,
        dicomBuilder,
        localDicomStore,
        isExportAllAnnotations: false,
        clinicInfo,
      });

      const syncRequestsEntries = Object.entries(syncRequests);
      const settledResults = await Promise.allSettled(syncRequestsEntries.map(([, req]) => req));

      const syncFailures: Record<string, PACSStoreError> = {};

      settledResults.forEach((result, index) => {
        const [key] = syncRequestsEntries[index];
        if (result.status === 'rejected') {
          syncFailures[key] = result.reason;
          console.error('Sync request failed:', result.reason);
        } else if (!isOk(result.value)) {
          syncFailures[key] = result.value.error;
          console.error('Sync request failed:', result.value.error);
        }
      });

      if (Object.keys(syncFailures).length > 0) {
        throw new PACSSyncFailuresError(syncFailures);
      }
    },
  });
}

export const useSyncStudyFromStoreWithPACSMutationState = ({ study }: { study: Study }) => {
  const mutationState = useMutationState({
    filters: {
      mutationKey: ['syncStudyFromStoreWithPACS'],
      predicate: (mutation: Mutation<void, Error, MutationFnVariables>) =>
        mutation.state.variables?.study._id === study._id,
    },
  });

  return mutationState;
};
